import { LanguageSwitcher } from '@components/ui/LanguageSwitcher';
import { SocialLinks } from '@components/ui/SocialLinks';
import { ThemeToggleButton, useThemeTransition } from '@components/ui/shadcn-io/theme-toggle-button';
import { getCompany, getNavigation, type ContactIconName } from '@content';
import useAnimationPreferences from '@hooks/use-animation-preferences';
import { useLocale } from '@hooks/use-locale';
import { useTheme } from '@hooks/use-theme';
import { cn } from '@lib/theme';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight, Mail, MapPin, Phone, type LucideIcon } from 'lucide-react';
import { useCallback } from 'react';

import WovenWordmark from './WovenWordmark';

const contactIcons: Record<ContactIconName, LucideIcon> = {
  phone: Phone,
  mail: Mail,
  'map-pin': MapPin,
};

export default function Footer() {
  const { locale, localizeHref } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { shouldAnimate } = useAnimationPreferences();
  const { startTransition } = useThemeTransition();
  const companyContent = getCompany(locale);
  const navigationContent = getNavigation(locale);
  const isTamil = locale === 'ta';
  const navColumns = [navigationContent.footer.slice(0, 4), navigationContent.footer.slice(4)];

  const handleThemeToggle = useCallback(() => {
    startTransition(() => toggleTheme());
  }, [startTransition, toggleTheme]);

  return (
    <footer className="relative overflow-hidden bg-[#090a17] text-[#f7f8ff]" role="contentinfo">
      <div className="border-b border-white/10 pt-10 sm:pt-14">
        <div className="container mx-auto px-5 sm:px-6 lg:px-10 xl:px-16">
          <div className="flex items-end justify-between gap-8">
            <div>
              <p className="text-brand-zari text-[0.65rem] font-bold tracking-[0.2em] uppercase">
                {isTamil ? 'ஒவ்வொரு நூலும் வீட்டை நோக்கி' : 'Every thread finds its way home'}
              </p>
              <p className="font-heading mt-3 max-w-2xl text-3xl leading-tight sm:text-4xl">
                {isTamil
                  ? 'ஒரு பெயர். ஐந்து தலைமுறைகள். எண்ணற்ற கதைகள்.'
                  : 'One name. Five generations. Countless stories.'}
              </p>
            </div>
            <p className="hidden text-right text-xs tracking-[0.16em] text-[#9ea7c2] uppercase lg:block">
              {isTamil ? 'ஜலகண்டபுரம் · தமிழ்நாடு' : 'Jalakandapuram · Tamil Nadu'}
            </p>
          </div>
        </div>
        <WovenWordmark shouldAnimate={shouldAnimate} />
      </div>

      <div className="container mx-auto px-5 pt-16 pb-7 sm:px-6 lg:px-10 lg:pt-20 xl:px-16">
        <div className="grid gap-14 border-b border-white/15 pb-16 lg:grid-cols-[1.2fr_1fr] lg:gap-24 lg:pb-20">
          <div>
            <p className="max-w-xl text-sm leading-7 text-[#bac4df]">
              {isTamil
                ? 'உங்கள் கொண்டாட்டத்தின் நிறம், மரபு, மற்றும் நினைவுகளை புரிந்து கொள்ளும் ஒரு தனிப்பட்ட ஆலோசனையுடன் தொடங்குங்கள்.'
                : 'Begin with a personal consultation shaped around the colour, ritual, and memories of your celebration.'}
            </p>
            <h2 className="font-heading mt-5 max-w-3xl text-4xl leading-[1.02] font-medium sm:text-5xl lg:text-6xl">
              {isTamil ? 'உங்கள் அடுத்த பட்டு கதைக்கு நேரம் ஒதுக்குவோம்.' : 'Let’s make time for your next silk story.'}
            </h2>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={localizeHref('/contact')}
                className="zari-hover group bg-brand-zari text-accent-foreground inline-flex min-h-12 items-center gap-3 rounded-full px-6 text-sm font-bold transition-colors hover:bg-[#efc96f]"
              >
                {isTamil ? 'ஆலோசனையை தொடங்குங்கள்' : 'Start a consultation'}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to={localizeHref('/collections')}
                className="hover:border-brand-zari hover:text-brand-zari inline-flex min-h-12 items-center rounded-full border border-white/35 px-6 text-sm font-semibold transition-colors"
              >
                {isTamil ? 'கலெக்ஷன்களை காணுங்கள்' : 'Explore collections'}
              </Link>
            </div>
          </div>

          <div className="grid gap-10 sm:grid-cols-2">
            {navColumns.map((column, columnIndex) => (
              <nav key={columnIndex} aria-label={columnIndex === 0 ? 'Shop and discover' : 'Company'}>
                <p className="text-brand-zari mb-4 text-xs font-bold tracking-[0.16em] uppercase">
                  {columnIndex === 0
                    ? isTamil
                      ? 'கண்டறியுங்கள்'
                      : 'Discover'
                    : isTamil
                      ? 'ஸ்ரீ சில்க்ஸ்'
                      : 'Sree Silks'}
                </p>
                <ul className="space-y-1" role="list">
                  {column.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={localizeHref(item.href)}
                        className="group hover:text-brand-zari inline-flex min-h-11 items-center gap-2 py-2 text-base text-[#e6eafa] transition-colors"
                      >
                        <span>{item.name}</span>
                        <ArrowUpRight className="h-3.5 w-3.5 translate-y-1 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        <div className="grid gap-10 border-b border-white/15 py-10 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            {companyContent.contactDetails.map((item) => {
              const Icon = contactIcons[item.icon];
              const content = (
                <>
                  <Icon className="text-brand-zari h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{item.content}</span>
                </>
              );

              return item.href ? (
                <a
                  key={item.id}
                  href={item.href}
                  className="inline-flex min-h-11 max-w-sm items-center gap-3 text-sm leading-6 text-[#bac4df] transition-colors hover:text-white"
                  aria-label={item.label}
                >
                  {content}
                </a>
              ) : (
                <div key={item.id} className="inline-flex max-w-sm items-start gap-3 text-sm leading-6 text-[#bac4df]">
                  {content}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 text-[#f7f8ff]">
            <SocialLinks />
            <ThemeToggleButton
              theme={theme}
              onClick={handleThemeToggle}
              className="hover:text-brand-zari text-foreground bg-background inline-flex h-11 w-11 rounded-full border-0 transition-colors hover:bg-white/10"
            />
          </div>
        </div>

        <div className="flex flex-col gap-5 pt-7 text-sm text-[#bac4df] md:flex-row md:items-center">
          <p>
            © {new Date().getFullYear()} Sree Silks.{' '}
            {isTamil ? 'அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.' : 'All rights reserved.'}
          </p>
          <div
            className={cn(
              'md:ml-auto',
              '[&_a]:text-[#bac4df] [&_a:hover]:text-[#071225]',
              '[&_a[data-status=active]]:text-accent-foreground',
              '[&_nav]:border-white/25'
            )}
          >
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  );
}
